import React, { useState, useEffect } from "react";
import { Modal, Button, Group, Stack, Text } from "@mantine/core";
import {
  IconCalendar,
  IconChevronLeft,
  IconChevronRight,
} from "@tabler/icons-react";
import "./CalendarFilterModal.css";

function CalendarFilterModal({ isOpen, onClose, currentDate, onDateSelect }) {
  const [viewDate, setViewDate] = useState(new Date(currentDate));
  const [selectedDate, setSelectedDate] = useState(new Date(currentDate));
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartDate, setDragStartDate] = useState(null);
  const [dragEndDate, setDragEndDate] = useState(null);

  useEffect(() => {
    if (isOpen && currentDate) {
      setViewDate(new Date(currentDate));
      setSelectedDate(new Date(currentDate));
      setDragStartDate(null);
      setDragEndDate(null);
    }
  }, [isOpen, currentDate]);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const getDaysInMonth = date => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = date => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(viewDate);
    const firstDay = getFirstDayOfMonth(viewDate);
    const days = [];

    // Add empty cells for days before the first day of month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const handlePreviousMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const handleMouseDown = day => {
    if (day) {
      const newDate = new Date(
        viewDate.getFullYear(),
        viewDate.getMonth(),
        day
      );
      setIsDragging(true);
      setDragStartDate(newDate);
      setDragEndDate(newDate);
      setSelectedDate(newDate);
    }
  };

  const handleMouseEnter = day => {
    if (isDragging && day) {
      const newDate = new Date(
        viewDate.getFullYear(),
        viewDate.getMonth(),
        day
      );
      setDragEndDate(newDate);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const isInRange = day => {
    if (!day || !dragStartDate || !dragEndDate) return false;
    const currentDate = new Date(
      viewDate.getFullYear(),
      viewDate.getMonth(),
      day
    );
    const start = dragStartDate < dragEndDate ? dragStartDate : dragEndDate;
    const end = dragStartDate < dragEndDate ? dragEndDate : dragStartDate;
    return currentDate >= start && currentDate <= end;
  };

  const handleApply = () => {
    // If a date range was selected, pass both start and end
    if (dragStartDate && dragEndDate) {
      const start = dragStartDate < dragEndDate ? dragStartDate : dragEndDate;
      const end = dragStartDate < dragEndDate ? dragEndDate : dragStartDate;

      // Pass range info to parent
      onDateSelect(start, end);
    } else if (selectedDate) {
      // Single date selected
      onDateSelect(selectedDate, null);
    }
  };

  const handleGoToToday = () => {
    const today = new Date();
    setViewDate(today);
    setSelectedDate(today);
  };

  const isToday = day => {
    if (!day) return false;
    const today = new Date();
    return (
      day === today.getDate() &&
      viewDate.getMonth() === today.getMonth() &&
      viewDate.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = day => {
    if (!day || !selectedDate) return false;
    return (
      day === selectedDate.getDate() &&
      viewDate.getMonth() === selectedDate.getMonth() &&
      viewDate.getFullYear() === selectedDate.getFullYear()
    );
  };

  const calendarDays = generateCalendarDays();

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconCalendar size={20} />
          <Text size="lg" fw={600}>
            Select Date
          </Text>
        </Group>
      }
      size="auto"
      centered
      classNames={{
        content: "calendar-filter-modal-content",
      }}
      styles={{
        content: {
          border: "none",
        },
        header: {
          borderBottom: "none",
        },
        body: {
          padding: 0,
        },
      }}
    >
      <div
        data-calendar-theme="teal"
        style={{
          padding: "1.5rem",
          "--mantine-primary-color": "teal",
          "--calendar-selected-bg": "var(--mantine-color-teal-6)",
          "--calendar-selected-border": "var(--mantine-color-teal-7)",
        }}
      >
        <Stack gap="lg">
          {/* Custom Calendar */}
          <div className="custom-calendar">
            {/* Calendar Header with Month/Year and Navigation */}
            <div className="calendar-header-controls">
              <Button
                variant="subtle"
                color="gray"
                size="sm"
                onClick={handlePreviousMonth}
                className="calendar-nav-button"
              >
                <IconChevronLeft size={18} />
              </Button>

              <Text size="lg" fw={600} className="calendar-month-year">
                {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
              </Text>

              <Button
                variant="subtle"
                color="gray"
                size="sm"
                onClick={handleNextMonth}
                className="calendar-nav-button"
              >
                <IconChevronRight size={18} />
              </Button>
            </div>

            {/* Weekday Headers */}
            <div className="calendar-weekdays">
              {dayNames.map(day => (
                <div key={day} className="calendar-weekday">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days Grid */}
            <div
              className="calendar-days-grid"
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {calendarDays.map((day, index) => (
                <div
                  key={index}
                  className={`calendar-day ${!day ? "empty" : ""} ${isToday(day) ? "today" : ""} ${isSelected(day) ? "selected" : ""} ${isInRange(day) ? "in-range" : ""}`}
                  onMouseDown={() => handleMouseDown(day)}
                  onMouseEnter={() => handleMouseEnter(day)}
                >
                  {day || ""}
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <Group justify="space-between">
            <Button
              variant="subtle"
              color="gray"
              onClick={handleGoToToday}
              leftSection={<IconCalendar size={16} />}
            >
              Go to Today
            </Button>
            <Group>
              <Button variant="default" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="filled"
                color="teal"
                onClick={handleApply}
                leftSection={<IconCalendar size={16} />}
              >
                Apply
              </Button>
            </Group>
          </Group>
        </Stack>
      </div>
    </Modal>
  );
}

export default CalendarFilterModal;
